import type {
  DataFetchParams,
  DataFetchResponse,
  FilterOptionsParams,
  DynamicFilterOptionsPage,
} from '@/components/DataTable';
import {
  listOrganizationPlugins,
  getPluginFilterOptions,
} from '@kirin/api-client/api/plugins';
import type {
  PluginListItem,
  PluginSortField,
  PluginFilterOptionField,
} from '@kirin/api-client/contracts/plugins';
import { apiClient } from '@/lib/api-client';

/**
 * Fetch organization plugins on the canonical DataTable contract. Filters are
 * the engine's canonical `FilterValue[]` passed straight through. Both the org
 * and personal tabs share this canonical path.
 */
export async function fetchOrgPlugins(
  organizationId: string,
  params: DataFetchParams
): Promise<DataFetchResponse<PluginListItem>> {
  const sortField = params.sort?.field as PluginSortField | undefined;
  const response = await listOrganizationPlugins(apiClient, organizationId, {
    page: params.pagination.pageIndex + 1,
    limit: params.pagination.pageSize,
    search: params.search || undefined,
    sortBy: sortField,
    sortOrder: params.sort?.order,
    filters: params.filters,
    knownTotal: params.knownTotal,
  });

  return {
    data: response.plugins,
    pagination: response.pagination,
    // The DataTable derives its "filters in effect" state from internal hooks,
    // not from the response, and the Plugins API doesn't echo filter metadata.
    meta: { activeFilters: [] },
  };
}

/**
 * Fetch dynamic filter options for one org Plugins column (Name / Marketplace /
 * Users), with server-side search and offset pagination. Mirrors
 * `fetchMCPFieldFilterOptions`.
 */
export async function fetchPluginFieldFilterOptions(
  organizationId: string,
  field: PluginFilterOptionField,
  params: FilterOptionsParams
): Promise<DynamicFilterOptionsPage> {
  const response = await getPluginFilterOptions(apiClient, organizationId, {
    field,
    search: params.search,
    limit: params.limit,
    offset: params.offset,
  });

  return {
    options: response.options,
    total: response.pagination.total,
    nextOffset: response.pagination.nextOffset ?? null,
    hasMore: response.pagination.hasMore,
  };
}
